/**
 * Marks each collapsed header chrome with data-over-dark from the
 * bar's corners and edge midpoints. Left and right are sampled separately.
 * Each probe reads the pixel the viewer sees at that point: the paint
 * stack is walked topmost-first and stops at the first opaque layer
 * (media pixel or CSS background, body included), blending any
 * translucent layers above it. Only the bar under test is excluded.
 * Media pixels come from a scaled snapshot of the full bitmap so that
 * srcset density variants cannot skew the sample coordinates.
 * Light local color keeps the black stroke; dark local color inverts it.
 * Set DEBUG below to draw the sample patch and each probe.
 */
(() => {
  // Flip to true to draw the sampling overlay (probe dots and verdicts).
  const DEBUG = false;

  const DARK = 0.4;

  // Returns every header bar that gets a dark/light verdict.
  const chrome = () => document.querySelectorAll("[data-nav-chrome]");

  // Parses an rgb()/rgba() string into { r, g, b, a }, or null.
  const parseRgba = (value) => {
    const m = String(value).match(
      /rgba?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*[, ]\s*([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)/,
    );
    if (!m) return null;
    const a = m[4] === undefined ? 1 : m[4].endsWith("%") ? Number.parseFloat(m[4]) / 100 : Number(m[4]);
    return { r: Number(m[1]), g: Number(m[2]), b: Number(m[3]), a };
  };

  // Converts a color to WCAG relative luminance, 0 (black) to 1 (white).
  const lum = ({ r, g, b }) => {
    const lin = (c) => {
      const n = c / 255;
      return n <= 0.03928 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  };

  // Resolves one object-position token (keyword, %, or px) to a pixel offset.
  const objectOffset = (token, extra) => {
    if (token === "left" || token === "top") return 0;
    if (token === "right" || token === "bottom") return extra;
    if (token === "center") return extra / 2;
    if (String(token).endsWith("%")) return (Number.parseFloat(token) / 100) * extra;
    const n = Number.parseFloat(token);
    return Number.isFinite(n) ? n : extra / 2;
  };

  // Maps a viewport point to its 0..1 position in the media's visible object-fit crop.
  const mediaSourcePoint = (media, vx, vy) => {
    const nw = media.naturalWidth || media.videoWidth;
    const nh = media.naturalHeight || media.videoHeight;
    if (!nw || !nh) return null;
    const cw = media.clientWidth;
    const ch = media.clientHeight;
    if (cw < 1 || ch < 1) return null;
    const rect = media.getBoundingClientRect();
    const x = vx - (rect.left + media.clientLeft);
    const y = vy - (rect.top + media.clientTop);
    if (x < 0 || y < 0 || x >= cw || y >= ch) return null;
    const fit = getComputedStyle(media).objectFit || "fill";
    let w = cw;
    let h = ch;
    if (fit === "cover" || fit === "contain" || fit === "scale-down") {
      const cover = Math.max(cw / nw, ch / nh);
      const contain = Math.min(cw / nw, ch / nh);
      const scale = fit === "cover" ? cover : fit === "scale-down" ? Math.min(1, contain) : contain;
      w = nw * scale;
      h = nh * scale;
    } else if (fit === "none") {
      w = nw;
      h = nh;
    }
    const parts = getComputedStyle(media).objectPosition.trim().split(/\s+/);
    const ox = objectOffset(parts[0] || "50%", cw - w);
    const oy = objectOffset(parts[1] || "50%", ch - h);
    const fx = (x - ox) / w;
    const fy = (y - oy) / h;
    if (fx < 0 || fy < 0 || fx >= 1 || fy >= 1) return null;
    return { fx, fy };
  };

  // Media pixels are read from a snapshot of the whole bitmap scaled into a
  // small canvas, indexed by fractional position. drawImage source rects use
  // raw bitmap pixels while naturalWidth reports the srcset density-corrected
  // size, so coordinate-space sampling drifts whenever the browser picks a
  // variant with density != 1 (e.g. a ?width=3000 candidate at sizes=100vw).
  // Scaling the full bitmap sidesteps that mismatch entirely.
  const MEDIA_SNAPSHOT = 300;
  let mediaPass = 0;
  const mediaCache = new WeakMap();

  // Returns cached ImageData of the media's full bitmap scaled to the snapshot canvas.
  const mediaPixels = (media) => {
    const key =
      media.tagName === "VIDEO" ? `video:${mediaPass}` : media.currentSrc || media.src;
    const cached = mediaCache.get(media);
    if (cached && cached.key === key) return cached.data;
    try {
      const canvas =
        mediaPixels.canvas || (mediaPixels.canvas = document.createElement("canvas"));
      const ctx =
        mediaPixels.ctx ||
        (mediaPixels.ctx = canvas.getContext("2d", { willReadFrequently: true }));
      canvas.width = MEDIA_SNAPSHOT;
      canvas.height = MEDIA_SNAPSHOT;
      ctx.clearRect(0, 0, MEDIA_SNAPSHOT, MEDIA_SNAPSHOT);
      ctx.drawImage(media, 0, 0, MEDIA_SNAPSHOT, MEDIA_SNAPSHOT);
      const data = ctx.getImageData(0, 0, MEDIA_SNAPSHOT, MEDIA_SNAPSHOT);
      mediaCache.set(media, { key, data });
      return data;
    } catch {
      return null;
    }
  };

  // Reads the media pixel visible at a viewport point as { r, g, b, a }.
  const sampleMedia = (media, vx, vy) => {
    const src = mediaSourcePoint(media, vx, vy);
    if (!src) return null;
    const pixels = mediaPixels(media);
    if (!pixels) return null;
    const px = Math.min(MEDIA_SNAPSHOT - 1, Math.floor(src.fx * MEDIA_SNAPSHOT));
    const py = Math.min(MEDIA_SNAPSHOT - 1, Math.floor(src.fy * MEDIA_SNAPSHOT));
    const d = pixels.data;
    const i = (py * MEDIA_SNAPSHOT + px) * 4;
    return { r: d[i], g: d[i + 1], b: d[i + 2], a: d[i + 3] / 255 };
  };

  // Parses any CSS color string to { r, g, b, a }, or null if transparent/invalid.
  const cssColor = (value) => {
    const parsed = parseRgba(value);
    if (parsed) return parsed.a > 0.01 ? parsed : null;
    if (!value || value === "transparent") return null;
    try {
      const canvas = cssColor.canvas || (cssColor.canvas = document.createElement("canvas"));
      const ctx =
        cssColor.ctx ||
        (cssColor.ctx = canvas.getContext("2d", { willReadFrequently: true }));
      canvas.width = 1;
      canvas.height = 1;
      ctx.clearRect(0, 0, 1, 1);
      ctx.fillStyle = value;
      ctx.fillRect(0, 0, 1, 1);
      const d = ctx.getImageData(0, 0, 1, 1).data;
      if (d[3] < 3) return null;
      return { r: d[0], g: d[1], b: d[2], a: d[3] / 255 };
    } catch {
      return null;
    }
  };

  // Composites a foreground color over a background color (Porter-Duff "over").
  const over = (src, dst) => {
    if (!src || src.a <= 0) return dst;
    if (!dst || dst.a <= 0) return { r: src.r, g: src.g, b: src.b, a: src.a };
    const a = src.a + dst.a * (1 - src.a);
    const mix = (s, d) => (s * src.a + d * dst.a * (1 - src.a)) / a;
    return { r: mix(src.r, dst.r), g: mix(src.g, dst.g), b: mix(src.b, dst.b), a };
  };

  // Returns the color a node paints at a point (media pixel or background), or null.
  const layerAt = (node, x, y) => {
    const style = getComputedStyle(node);
    const opacity = Number(style.opacity);
    if (opacity <= 0 || style.visibility === "hidden") return null;

    if (node.tagName === "IMG" || node.tagName === "VIDEO") {
      const sampled = sampleMedia(node, x, y);
      if (!sampled || sampled.a <= 0) return null;
      return { ...sampled, a: sampled.a * opacity, kind: node.tagName.toLowerCase() };
    }

    const bg = cssColor(style.backgroundColor);
    if (!bg) return null;
    return { ...bg, a: bg.a * opacity, kind: "bg" };
  };

  // True for nodes the probe must skip: the bar under test and the debug overlay.
  const ignored = (node, el) =>
    el.contains(node) ||
    node.id === "nav-stroke-debug" ||
    Boolean(node.closest && node.closest("#nav-stroke-debug"));

  // Walks the paint stack at a point, topmost first, blending translucent layers
  // until one is opaque, and returns the visible { lum, kind } there.
  const probeAt = (x, y, el) => {
    const stack = document.elementsFromPoint(x, y).filter((node) => !ignored(node, el));
    let color = null;
    const kinds = [];
    for (const node of stack) {
      const layer = layerAt(node, x, y);
      if (!layer) continue;
      color = over(color, layer);
      kinds.push(layer.kind);
      if (color.a >= 0.99) break;
    }
    if (!color || color.a < 0.05) return { lum: null, kind: "—" };
    const kind = kinds.length > 1 ? "mix" : kinds[0];
    return { lum: lum(color), kind };
  };

  // Returns the 8 probe points on a rect: corners plus edge midpoints, inset 1px.
  const pointsOnRect = (r) => {
    const inset = 1;
    const left = r.left + inset;
    const right = r.right - inset;
    const top = r.top + inset;
    const bottom = r.bottom - inset;
    const midX = r.left + r.width / 2;
    const midY = r.top + r.height / 2;
    return [
      [left, top],
      [midX, top],
      [right, top],
      [right, midY],
      [right, bottom],
      [midX, bottom],
      [left, bottom],
      [left, midY],
    ];
  };

  // Probes all points around a bar and returns its verdict from the median luminance.
  const sampleChrome = (el) => {
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return { dark: false, median: null, box: r, probes: [] };
    const probes = pointsOnRect(r).map(([x, y]) => {
      const px = Math.min(window.innerWidth - 1, Math.max(0, x));
      const py = Math.min(window.innerHeight - 1, Math.max(0, y));
      const hit = probeAt(px, py, el);
      return { x: px, y: py, ...hit, dark: hit.lum != null && hit.lum < DARK };
    });
    const lums = probes.map((p) => p.lum).filter((value) => value != null).sort((a, b) => a - b);
    const mid = (lums.length - 1) / 2;
    const median = lums.length
      ? (lums[Math.floor(mid)] + lums[Math.ceil(mid)]) / 2
      : null;
    const dark = median != null && median < DARK;
    return { dark, median, box: r, probes };
  };

  // Creates (or returns) the fixed full-screen root for the debug overlay.
  const overlay = () => {
    let root = document.getElementById("nav-stroke-debug");
    if (root) return root;
    root = document.createElement("div");
    root.id = "nav-stroke-debug";
    root.setAttribute("aria-hidden", "true");
    Object.assign(root.style, {
      position: "fixed",
      inset: "0",
      zIndex: "9999",
      pointerEvents: "none",
      font: "10px/1.2 ui-monospace, monospace",
    });
    document.body.appendChild(root);
    return root;
  };

  // Renders the debug overlay: sample area, verdict badge, and labeled probe dots.
  const draw = (samples) => {
    const root = overlay();
    root.replaceChildren();
    samples.forEach(({ dark, median, box, probes }) => {
      if (box.width < 1) return;
      const xs = probes.map((p) => p.x);
      const ys = probes.map((p) => p.y);
      const area = document.createElement("div");
      const pad = 8;
      const left = Math.min(...xs) - pad;
      const top = Math.min(...ys) - pad;
      Object.assign(area.style, {
        position: "absolute",
        left: `${left}px`,
        top: `${top}px`,
        width: `${Math.max(...xs) - Math.min(...xs) + pad * 2}px`,
        height: `${Math.max(...ys) - Math.min(...ys) + pad * 2}px`,
        border: `1px dashed ${dark ? "#f44" : "#0c0"}`,
        background: dark ? "rgba(255,0,0,0.12)" : "rgba(0,255,0,0.12)",
      });
      root.appendChild(area);

      const verdict = document.createElement("div");
      Object.assign(verdict.style, {
        position: "absolute",
        left: `${box.left}px`,
        top: `${box.bottom + 28}px`,
        padding: "2px 4px",
        background: dark ? "#f44" : "#0c0",
        color: "#000",
        fontWeight: "700",
      });
      const label = median == null ? "—" : median.toFixed(2);
      verdict.textContent = dark
        ? `DARK ${label} → invert stroke`
        : `LIGHT ${label} → keep stroke`;
      root.appendChild(verdict);

      probes.forEach((p) => {
        const dot = document.createElement("div");
        const fill = p.lum == null ? "#888" : p.dark ? "#f44" : "#0c0";
        Object.assign(dot.style, {
          position: "absolute",
          left: `${p.x - 4}px`,
          top: `${p.y - 4}px`,
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          background: fill,
          boxShadow: "0 0 0 1px #fff",
        });
        root.appendChild(dot);

        const label = document.createElement("div");
        Object.assign(label.style, {
          position: "absolute",
          left: `${p.x + 6}px`,
          top: `${p.y - 6}px`,
          padding: "1px 3px",
          background: "rgba(0,0,0,0.7)",
          color: "#fff",
          whiteSpace: "nowrap",
        });
        label.textContent =
          p.lum == null ? `${p.kind}` : `${p.lum.toFixed(2)} ${p.kind}`;
        root.appendChild(label);
      });
    });
  };

  // Samples every bar, toggles data-over-dark, and redraws the overlay if debugging.
  const update = () => {
    mediaPass += 1;
    const samples = [...chrome()].map((el) => {
      const sample = sampleChrome(el);
      el.toggleAttribute("data-over-dark", sample.dark);
      return sample;
    });
    if (DEBUG) draw(samples);
  };

  let frame = 0;

  // Queues one update on the next animation frame, coalescing repeat calls.
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      update();
    });
  };

  // Runs an initial update and watches each bar for size changes, once per bar.
  const bind = () => {
    update();
    chrome().forEach((el) => {
      if (el.dataset.navBound) return;
      el.dataset.navBound = "true";
      new ResizeObserver(schedule).observe(el);
    });
  };

  bind();
  window.addEventListener("scroll", schedule, { passive: true, capture: true });
  window.addEventListener("resize", schedule);
  window.addEventListener("load", schedule);
  document.addEventListener("shopify:section:load", bind);
})();
